/*
 * This context is for holding various metadata that should be consistent across
 * All prosemirror editors on the EditPage (for example, whether or not to display
 * inline annotations).
 */
import { createContext, useContext, useState, useRef, createRef } from 'react';
import { WebsocketProvider } from 'y-websocket';

type UserType = {
  color: string;
  highlightedColor: string;
};

export type UserAvatarType = {
  name: string;
  avatarUrl: string;
  contentlyEmployee: string;
  joinedAt: Date;
  managingEditor: string;
  color: string;
  highlightedColor: string;
};

export type EditorPageStateType = {
  showComments: boolean;
  zenMode?: boolean;
  user?: UserType;
  awareness?: WebsocketProvider["awareness"];
};

type EditorStateType = {
  editorPageState?: EditorPageStateType;
  setEditorPageState?: React.Dispatch<React.SetStateAction<EditorPageStateType>>;
  currentlyEditingUsers: UserAvatarType[];
  updateCurrentlyEditingUsers: React.Dispatch<React.SetStateAction<UserAvatarType[]>>;
  userColorRef: React.MutableRefObject<UserColorType | null>;
  activeAnnotationId: string | null;
  setActiveAnnotationId: React.Dispatch<React.SetStateAction<string | null>>;
  activeCommentId: string | null;
  setActiveCommentId: React.Dispatch<React.SetStateAction<string | null>>;
};

type UserColorType = {
  color: string;
  highlightedColor: string;
};

export const EditorContext = createContext<EditorStateType>({
  editorPageState: { showComments: false, zenMode: false },
  setEditorPageState: () => {},
  currentlyEditingUsers: [],
  updateCurrentlyEditingUsers: () => {},
  userColorRef: createRef(),
  activeAnnotationId: null,
  setActiveAnnotationId: () => {},
  activeCommentId: null,
  setActiveCommentId: () => {}
});

export const EditorProvider: React.FC = ({ children }) => {
  const [currentlyEditingUsers, updateCurrentlyEditingUsers] = useState<UserAvatarType[]>([]);
  const [editorPageState, setEditorPageState] = useState<EditorPageStateType>({ showComments: true, zenMode: false });
  const userColorRef = useRef<UserColorType | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);

  return (
    <EditorContext.Provider value={{
      editorPageState,
      setEditorPageState,
      currentlyEditingUsers,
      updateCurrentlyEditingUsers,
      userColorRef,
      activeAnnotationId,
      setActiveAnnotationId,
      activeCommentId,
      setActiveCommentId
      }}>
      {children}
    </EditorContext.Provider>
  );
};

export const useEditorContext = () => {
  return useContext<EditorStateType>(EditorContext);
};

